import React from 'react';

/**
 * Prevent text orphans by replacing the last space with a non-breaking space
 * @param {string} text - The input text to process
 * @param {number} minWords - Minimum number of words to keep together (default: 3)
 * @returns {string} Text with orphans prevented
 */
export const preventOrphans = (text, minWords = 3) => {
  if (!text) return text;

  const words = text.trim().split(/\s+/);

  if (words.length > minWords) {
    // Join the last 3 words with non-breaking spaces
    const lastThreeWords = words.slice(-3).join('\u00A0');
    const remainingText = words.slice(0, -3).join(' ');

    return `${remainingText} ${lastThreeWords}`;
  }

  return text;
};

/**
 * React hook to prevent orphans in text elements
 * @param {string} text - The text to process
 * @param {number} minWords - Minimum number of words to keep together (default: 3)
 * @returns {string} Text with orphans prevented
 */
export const usePreventOrphans = (text, minWords = 3) => {
  return preventOrphans(text, minWords);
};

/**
 * Parse markdown-style formatting (bold and italic) and return JSX elements
 * Supports: **bold** and *italic*
 * @param {string} text - The text with markdown formatting
 * @returns {Array} Array of React elements/strings
 */
export const parseMarkdown = (text) => {
  if (!text) return text;

  const parts = [];
  let remaining = text;
  let key = 0;

  // Pattern: Match **bold** or *italic* (bold must be checked first)
  const pattern = /(\*\*.*?\*\*|\*.*?\*)/g;
  let match;
  let lastIndex = 0;

  while ((match = pattern.exec(remaining)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(remaining.substring(lastIndex, match.index));
    }

    const matched = match[0];
    
    // Check if it's bold (**text**)
    if (matched.startsWith('**') && matched.endsWith('**')) {
      const content = matched.slice(2, -2);
      parts.push(<strong key={key++}>{content}</strong>);
    }
    // Otherwise it's italic (*text*)
    else if (matched.startsWith('*') && matched.endsWith('*')) {
      const content = matched.slice(1, -1);
      parts.push(<em key={key++}>{content}</em>);
    }

    lastIndex = pattern.lastIndex;
  }

  // Add remaining text
  if (lastIndex < remaining.length) {
    parts.push(remaining.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};
